import type { CSSProperties } from "react";

// The reduced-motion rule travels with the component. app/globals.css calms CSS
// animation globally, but the registry bundles imports only, so an installed
// copy never receives that reset.
//
// `!important` because the sweep is an inline style, which outranks a plain rule
// in a media query. It selects a marker class carried by TEXT_SHIMMER_CLASS_NAME
// so it also reaches consumers that build their own span out of these exports.
export const TEXT_SHIMMER_KEYFRAMES =
  "@keyframes beui-text-shimmer{from{background-position:200% 0}to{background-position:-200% 0}}" +
  "@media (prefers-reduced-motion: reduce){.beui-text-shimmer{animation:none !important}}";

// Die shadcn-Tokens dieses Projekts halten *Tripel* (`222.2 84% 4.9%`), keine
// fertigen Farben — `var(--foreground)` allein ergäbe im Verlauf einen
// ungültigen Farbwert, und der Text bliebe (wegen `text-transparent`)
// unsichtbar. Deshalb hier wie überall sonst im Portal in `hsl()` gewickelt.
//
// Kräftiger als die Vorlage (29.09.2026): Der Grund ist aufgehellt
// (`muted-foreground` zu 55 %), das Glanzband schmaler (40–60 % statt 30–70 %)
// und in voller Vordergrundfarbe — der Lichtstreif hebt sich deutlich ab,
// statt nur eine Nuance über den Text zu wandern.
//
// Die Hintergrundbreite bleibt 200 %: Die Keyframes verschieben um 200 % → -200 %,
// das sind bei 200 % Breite genau zwei Wiederholungen des Verlaufs — die Schleife
// schließt nahtlos. Bei 250 % waren es 2,4, und bei jedem Neustart sprang der
// Streifen von etwa drei Vierteln zurück an den Anfang.
export const TEXT_SHIMMER_CLASS_NAME =
  "beui-text-shimmer bg-[length:200%_100%] bg-clip-text text-transparent bg-[linear-gradient(110deg,hsl(var(--muted-foreground)/0.55)_38%,hsl(var(--foreground))_50%,hsl(var(--muted-foreground)/0.55)_62%)]";

/**
 * Puts the keyframes into the document once. A `<style>` per shimmer meant a
 * new style element — and a style recalculation that can stall running
 * animations — every time a shimmer mounted, e.g. on each thinking-word swap.
 */
export function ensureTextShimmerKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("beui-text-shimmer-keyframes")) return;
  const style = document.createElement("style");
  style.id = "beui-text-shimmer-keyframes";
  style.textContent = TEXT_SHIMMER_KEYFRAMES;
  document.head.appendChild(style);
}

/**
 * Delay that puts a shimmer mounted *now* at the phase of a shared page clock.
 * Every shimmer on the page runs in step, and one that replaces another (a new
 * thinking word, a remount) continues the sweep instead of restarting it from
 * the left edge — the visible hitch. Take it once per mount: changing
 * `animation-delay` on a running animation re-seeks it.
 */
export function textShimmerPhaseDelay(duration: number): number {
  if (typeof performance === "undefined" || duration <= 0) return 0;
  return -((performance.now() / 1000) % duration);
}

export function textShimmerStyle(duration: number, delay = 0): CSSProperties {
  return {
    animation: `beui-text-shimmer ${duration}s linear ${delay}s infinite`,
  };
}
